import * as React from "react";
import styled from "@emotion/styled";
import {
  Button,
  Checkbox,
  DatePicker,
  FieldError,
  Fieldset,
  FlexRow,
  Form,
  H2,
  Input,
  Label,
  Legend,
  RequiredText,
  RichTextEditor,
  Select,
  Stack,
  Text,
  UnexpectedError,
} from "atoms";
import { ImageUpload } from "features/images";
import { fieldOptions, validators } from "utils/form";
import type { OpenCall } from "../types";
import { useOpenCallForm } from "../api/useOpenCallForm";

const statuses = ["Draft", "Published", "Archived"] as const;
const partnerLinks = ["Website", "Twitter", "Instagram"] as const;

type OpenCallFormProps = {
  openCall?: OpenCall;
  onCancel?: VoidFunction;
};

function OpenCallForm({ openCall, onCancel }: OpenCallFormProps) {
  const {
    isEdit,
    formState: { errors },
    onSubmit,
    register,
    control,
    setValue,
    watch,
    mutation: { isLoading, error: mutationError },
  } = useOpenCallForm({
    openCall,
    onSuccess: onCancel,
  });

  // Watch checkbox values to dynamically modify form fields
  const isCommunityCuration = watch("isCommunityCuration");
  const enablePartnerCuration = watch("enablePartnerCuration");
  const isCommercial = watch("isCommercial");

  // Delete community voting dates when it's disabled
  React.useEffect(() => {
    if (!isCommunityCuration) {
      setValue("communityVotingStartDate", "");
      setValue("communityVotingEndDate", "");
    }
  }, [isCommunityCuration, setValue]);

  // Delete partner curation options when it's disabled
  React.useEffect(() => {
    if (!enablePartnerCuration) {
      setValue("isBlindPartnerCuration", false);
      setValue("showPartnerCommunityVotes", false);
    }
  }, [enablePartnerCuration, setValue]);

  // Require country of residence when OC is marked as commercial
  React.useEffect(() => {
    if (isCommercial) {
      setValue("requireCountryOfResidence", true);
    }
  }, [isCommercial, setValue]);

  // TODO - Other TODO Statements about Validation Below

  return (
    <SForm onSubmit={onSubmit}>
      <SH2>Open Call Details</SH2>

      <Checkbox
        {...register("isFeatured")}
        label={<strong>Feature this open call on the landing page</strong>}
      />

      <Label>
        Open Call Slug <RequiredText />
        <Input
          {...register("id", {
            ...fieldOptions.required,
            validate: validators.validSlug,
          })}
          disabled={isEdit}
        />
        {errors.id && <FieldError>{errors.id.message}</FieldError>}
      </Label>

      <Label>
        Status <RequiredText />
        <Select {...register("status", fieldOptions.required)}>
          {statuses.map((status) => (
            <option key={status} value={status.toUpperCase()}>
              {status}
            </option>
          ))}
        </Select>
        {errors.status && <FieldError>{errors.status.message}</FieldError>}
      </Label>

      <Label>
        Open Call Title <RequiredText />
        <Input {...register("title", fieldOptions.required)} />
        {errors.title && <FieldError>{errors.title.message}</FieldError>}
      </Label>

      <Label>
        Short Description <RequiredText />
        <Input
          {...register("subtitle", {
            ...fieldOptions.required,
            ...fieldOptions.maxLength(80),
          })}
        />
        {errors.subtitle && <FieldError>{errors.subtitle.message}</FieldError>}
      </Label>

      <Label>
        Full Description <RequiredText />
        <RichTextEditor control={control} name="description" required />
        {errors.description && (
          <FieldError>{errors.description.message}</FieldError>
        )}
      </Label>

      <ImageUpload
        control={control}
        fieldName="heroImage"
        required
        aspect={1}
        label={
          <>
            Open Call Image <RequiredText />
          </>
        }
        errorMessage={errors.heroImage?.url?.message}
        src={openCall?.heroImage}
      />

      <ImageUpload
        control={control}
        fieldName="badgeImage"
        aspect={1}
        label={<>Badge Image</>}
        errorMessage={errors.badgeImage?.url?.message}
        src={openCall?.badgeImage}
      />

      <SH2>Submission Options</SH2>
      <Stack gap="sm">
        <Checkbox
          {...register("isExclusive")}
          label={<strong>Requires invitation to submit</strong>}
        />
        <Checkbox
          {...register("showSubmissions")}
          label={<strong>Show submissions to the public</strong>}
        />
        <Checkbox
          {...register("isCommercial")}
          label={<strong>Mark Open Call as commercial</strong>}
        />
        <Checkbox
          {...register("requireCountryOfResidence")}
          label={
            <>
              <strong>Submissions must include a country of residence</strong>
              <br />
              <em>(Required for commercial Open Calls)</em>
            </>
          }
          disabled={isCommercial}
        />
      </Stack>

      <Label>
        Submission Requirements
        <RichTextEditor control={control} name="requirements" />
        {errors.requirements && (
          <FieldError>{errors.requirements.message}</FieldError>
        )}
      </Label>

      <Fieldset hasBorder>
        <Legend>Submission Dates</Legend>
        <FlexRow>
          <Label>
            Start Date <RequiredText />
            <DatePicker
              {...register("submissionStartDate", fieldOptions.required)}
            />
            {errors.submissionStartDate && (
              <FieldError>{errors.submissionStartDate.message}</FieldError>
            )}
          </Label>
          <Label>
            End Date
            <DatePicker {...register("submissionEndDate")} />
            {errors.submissionEndDate && (
              <FieldError>{errors.submissionEndDate.message}</FieldError>
            )}
          </Label>
        </FlexRow>
      </Fieldset>

      <SH2>Community Voting Options</SH2>
      <Stack gap="sm">
        <Checkbox
          {...register("isCommunityCuration")}
          label={<strong>The HUG Community will vote on this Open Call</strong>}
        />
      </Stack>

      {isCommunityCuration && (
        <Fieldset hasBorder>
          <Legend>Community Voting Dates</Legend>
          <FlexRow>
            <Label>
              Start Date
              {/* TODO: add validation */}
              <DatePicker {...register("communityVotingStartDate")} />
              {errors.communityVotingStartDate && (
                <FieldError>
                  {errors.communityVotingStartDate.message}
                </FieldError>
              )}
            </Label>

            <Label>
              End Date
              {/* TODO: add validation */}
              <DatePicker {...register("communityVotingEndDate")} />
              {errors.communityVotingEndDate && (
                <FieldError>{errors.communityVotingEndDate.message}</FieldError>
              )}
            </Label>
          </FlexRow>
        </Fieldset>
      )}

      <SH2>Partner Curation Options</SH2>
      <Stack gap="sm">
        <Checkbox
          {...register("enablePartnerCuration")}
          label={
            <strong>
              A partner curator will select submissions for this Open Call
            </strong>
          }
        />

        {enablePartnerCuration && (
          <>
            <Checkbox
              {...register("isBlindPartnerCuration")}
              label={<strong>Hide Artist details</strong>}
            />
            <Checkbox
              {...register("showPartnerCommunityVotes")}
              label={<strong>Show Community Favorites</strong>}
            />
          </>
        )}
      </Stack>

      <SH2>Selection options</SH2>

      <Label>
        Number of Winners <RequiredText />
        <Input
          {...register("maxWinners", {
            ...fieldOptions.required,
            min: { value: 1, message: "You need to have at least 1 winner." },
          })}
          type="number"
        />
        {errors.maxWinners && (
          <FieldError>{errors.maxWinners.message}</FieldError>
        )}
      </Label>

      <Label>
        Number of Community Favorites <RequiredText />
        <Input
          {...register("maxCommunityFavorites", fieldOptions.required)}
          type="number"
        />
        {errors.maxCommunityFavorites && (
          <FieldError>{errors.maxCommunityFavorites.message}</FieldError>
        )}
      </Label>

      <Checkbox
        {...register("isSelectionCompleted")}
        label={<strong>The selection process is complete</strong>}
      />

      <SH2>Exhibition Details</SH2>
      <Fieldset hasBorder>
        <Legend>Exhibition Dates</Legend>
        <FlexRow>
          <Label>
            Start date
            {/* TODO: add validation */}
            <DatePicker {...register("exhibitionStartDate")} />
            {errors.exhibitionStartDate && (
              <FieldError>{errors.exhibitionStartDate.message}</FieldError>
            )}
          </Label>

          <Label>
            End date
            {/* TODO: add validation */}
            <DatePicker {...register("exhibitionEndDate")} />
            {errors.exhibitionEndDate && (
              <FieldError>{errors.exhibitionEndDate.message}</FieldError>
            )}
          </Label>
        </FlexRow>
      </Fieldset>

      <SH2>Partner Details</SH2>

      <Label>
        Partner Name
        <Input {...register("partnerName")} />
        {errors.partnerName && (
          <FieldError>{errors.partnerName.message}</FieldError>
        )}
      </Label>

      <ImageUpload
        control={control}
        fieldName="partnerLogo"
        label={<>Affiliation Logo</>}
        errorMessage={errors.partnerLogo?.url?.message}
        src={openCall?.partnerLogo}
        aspect={1}
      />

      <Label>
        Partner Description
        <RichTextEditor control={control} name="partnerDescription" />
        {errors.partnerDescription && (
          <FieldError>{errors.partnerDescription.message}</FieldError>
        )}
      </Label>

      {partnerLinks.map((type, index) => (
        <Label key={type}>
          Partner {type}
          <Input
            {...register(`partnerLinks.${index}.url`, {
              validate: validators.validURL,
            })}
          />
          <Input
            {...register(`partnerLinks.${index}.type`)}
            type="hidden"
            value={type.toLowerCase()}
          />
          {errors.partnerLinks?.[index]?.url && (
            <FieldError>
              {errors.partnerLinks?.[index]?.url?.message}
            </FieldError>
          )}
        </Label>
      ))}

      <SH2>Submission Prompt</SH2>

      <Text>
        Use a prompt to collection additional information during submission.
      </Text>

      <Label>
        Private Submission Prompt (Responses Visible to HUG Only)
        <Input {...register("submissionPrompt")} />
        {errors.submissionPrompt && (
          <FieldError>{errors.submissionPrompt.message}</FieldError>
        )}
      </Label>

      <Label>
        Voter Submission Prompt (Responses Visible while Voting)
        <Input {...register("voterSubmissionPrompt")} />
        {errors.submissionPrompt && (
          <FieldError>{errors.submissionPrompt.message}</FieldError>
        )}
      </Label>

      <SH2>Paid Submissions</SH2>

      <FlexRow>
        <Button disabled={isLoading} type="submit">
          <span>{isEdit ? "Update" : "Create"}</span> Open Call
        </Button>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
      </FlexRow>

      {!!mutationError && <UnexpectedError error={mutationError} />}
    </SForm>
  );
}

const SForm = styled(Form)({
  maxWidth: 600,
  paddingBottom: "4rem",
});

const SH2 = styled(H2)({
  borderBottom: "2px solid currentColor",
  marginBottom: "1rem",
  paddingBottom: ".25em",
  paddingTop: "2rem",
});
SH2.defaultProps = {
  size: "lg",
};

export { OpenCallForm };
